import React from 'react';

import { PublicPage } from 'api/website';
import { AmpContentBlock } from 'cms/blocks/Amp/AmpContentBlock';
import { RecommendedJobsBlock } from 'cms/blocks/RecommendedJobs/RecommendedJobsBlock';
import {
  getContentOnlySearchResults,
  getContentPageUrl,
  getIsContentFullWidthLayout,
} from 'cms/helpers';
import {
  useContentHasPostSearchToolbar,
  useContentHomePageMeta,
} from 'cms/hooks';
import { AmpPostSearchToolbar } from 'components/AmpPostSearchToolbar';
import { Box } from 'components/Box';
import { PrimaryButtonLink, SecondaryButtonLink } from 'components/Button';
import { CmsSocialButtons } from 'components/CmsSocialButtons';
import { Grid, GridItem } from 'components/Grid';
import { FilestackImage } from 'components/Image';
import { ConditionalLink, Link } from 'components/Link';
import { Body, BodySmall, H1, H4, HeadingSmall } from 'components/Text';
import { colors, gutterWidth, maxWidth } from 'theme';
import { humanDateTime } from 'utils/date';
import { getRgbaString } from 'utils/style';

type Props = {
  content: PublicPage;
  searchMode: boolean;
};

export function AmpContentPage({ content, searchMode }: Props) {
  const pageUrl = getContentPageUrl({ content });
  const { homePageUrl } = useContentHomePageMeta({ content });
  const fullWidthLayout = getIsContentFullWidthLayout({ content });

  const resultsPage = 0;

  const hasPostSearchToolbar = useContentHasPostSearchToolbar({ content });
  const onlySearchResults = getContentOnlySearchResults({ content });
  if (onlySearchResults) {
    return null;
  }

  return (
    <>
      {hasPostSearchToolbar && (
        <AmpPostSearchToolbar
          content={content}
          searchMode={searchMode}
          homePage={content.subsite.homePage}
        />
      )}
      {!(content.heroSearch && content.heroSearch.onlyResults) && (
        <Box
          position="relative"
          maxWidth={maxWidth}
          py={[gutterWidth * 1.25, null, null, gutterWidth * 2]}
          m="0 auto"
          data-qa-id="amp-content"
          textAlign={content.type === 'PAGE' ? 'center' : undefined}
        >
          <Grid>
            <GridItem columnStart={[1, 1, 1, 2]} columnEnd={[13, 13, 13, 12]}>
              {searchMode ? (
                <>
                  <amp-list
                    id="article_results_list"
                    layout="fixed-height"
                    height="450"
                    src={`/data/website/search/posts/${content.subsite.id}/${CURRENT_LOCALE}/${resultsPage}`}
                    items="results.hits"
                    binding="always"
                  >
                    <template type="amp-mustache">
                      <Box
                        display="inline-block"
                        width={[1, 1 / 2, 1 / 3]}
                        verticalAlign="top"
                        maxWidth={[320, 360]}
                        mx="auto"
                        px={[0, gutterWidth / 2]}
                        py={gutterWidth / 2}
                        textAlign="left"
                      >
                        <Box
                          component="a"
                          href="/{{url}}"
                          display="block"
                          mb={24}
                          borderRadius={8}
                          overflow="hidden"
                          bg={colors.platinum}
                        >
                          <div data-style="padding-bottom: 56.25%; width: 100%; height: 100%; background-size: cover; background-position: center; background-image: url(https://process.filestackapi.com/quality=value:70/cache=expiry:31536000/compress/no_metadata/{{thumbnail.handle}})" />
                        </Box>
                        <H4 mt={0} mb={16} lineHeight={1.375}>
                          <a href="/{{url}}">{'{{title}}'}</a>
                        </H4>
                      </Box>
                    </template>
                  </amp-list>
                  <Box
                    position="relative"
                    zIndex={1}
                    pt={100}
                    mt={-100}
                    backgroundImage={`linear-gradient(to top, ${
                      colors.fog
                    } 0%, ${colors.fog} 50%, rgba(${getRgbaString(
                      colors.fog,
                      0
                    )}) 100%)`}
                  >
                    <SecondaryButtonLink size="large" href={`/${pageUrl}?pq=`}>
                      {getText('Show More')}
                    </SecondaryButtonLink>
                  </Box>
                </>
              ) : (
                <>
                  <H1>{content.title}</H1>
                  {(content.author ||
                    (content.postedDate && content.showDate)) && (
                    <Box mt={4}>
                      {content.author && content.author.name && (
                        <H4 data-qa-id="author-name" m={0} lineHeight={1}>
                          <ConditionalLink
                            data-qa-id="author-name-link"
                            link={
                              homePageUrl && content.author.slug
                                ? `/${homePageUrl}?author.slug=${content.author.slug}&pq=`
                                : ''
                            }
                          >
                            <Body lineHeight={1.33}>{content.author.name}</Body>
                          </ConditionalLink>
                        </H4>
                      )}
                      {content.showDate && (
                        // @ts-expect-error Fix type
                        <BodySmall tag="time" dateTime={content.postedDate}>
                          {humanDateTime(content.postedDate)}
                        </BodySmall>
                      )}
                    </Box>
                  )}
                  {content.headerImage && (
                    <Box mt={4}>
                      <FilestackImage
                        data={content.headerImage}
                        alt={content.headerImage.altText || content.title}
                        height={400}
                        width={fullWidthLayout ? 1152 : 952}
                      />
                    </Box>
                  )}
                  {content.contentBlocks.slice(0, 10).map((block, index) =>
                    block.type === 'ANCHOR' ? (
                      <div
                        id={`content-anchor-${block.data.anchor}`}
                        key={block.id}
                      />
                    ) : (
                      <Box
                        my={[gutterWidth * 1.25, null, null, gutterWidth * 2]}
                        key={block.id}
                        id={`cms-block-${block.id}`}
                        display={
                          block.type === 'FREE_CODE'
                            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (!block.data.isVisible as any)
                            : undefined
                        }
                      >
                        <AmpContentBlock
                          content={content}
                          block={block}
                          blockIndex={index}
                        />
                      </Box>
                    )
                  )}

                  {content.contentBlocks.length > 10 && (
                    <Box mt={64}>
                      <PrimaryButtonLink href={`/${pageUrl}`}>
                        {getText('View Full Page')}
                      </PrimaryButtonLink>
                    </Box>
                  )}
                  {content.hasRecommendedJobs && (
                    <RecommendedJobsBlock
                      context="amp"
                      data={{
                        headline: content.recommendedJobsHeadline,
                        layout: 'REGULAR',
                        jobFunction: undefined,
                      }}
                      fromCheckbox
                    />
                  )}
                  {(content.hasSocialSharing ||
                    (content.subsite.canSubscribe && content.type === 'POST') ||
                    (content.tags && content.tags.length > 0)) && (
                    <Box mt={64} id="subscribe">
                      {(content.hasSocialSharing ||
                        (content.subsite.canSubscribe &&
                          content.type === 'POST')) && (
                        <Box classes={['flex']} m="-6px" mt={32}>
                          {content.hasSocialSharing && (
                            <CmsSocialButtons
                              content={content}
                              message={content.metaDescription}
                              title={content.title}
                              subsite={content.subsite.publicName}
                            />
                          )}
                          {content.subsite.canSubscribe &&
                            content.type === 'POST' && (
                              <Box m="6px">
                                <Box
                                  component={SecondaryButtonLink}
                                  classes={['flex', 'align-center']}
                                  height={32}
                                  size="small"
                                  href={`/${pageUrl}?subscribe=true#subscribe`}
                                >
                                  {getText('Subscribe')}
                                </Box>
                              </Box>
                            )}
                        </Box>
                      )}
                      {content.tags && content.tags.length > 0 && (
                        <Box
                          mt={32}
                          classes={['flex']}
                          component={HeadingSmall}
                        >
                          {getText('Tags: ')}
                          {content.tags.map((tag) => (
                            <Box ml={12}>
                              <Box
                                component={homePageUrl ? Link : undefined}
                                href={
                                  homePageUrl
                                    ? `/${homePageUrl}?tags.slug=${tag.slug}&pq=`
                                    : undefined
                                }
                              >
                                {tag.tag}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </GridItem>
          </Grid>
        </Box>
      )}
    </>
  );
}
